import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useChatOpeningAsked } from './chatOpeningAsk.ts';
import type { AnsweringOpening, PageOpening } from './chatPageOpening.ts';

export interface ChatPageOpeningOptions {
  opening: PageOpening | null;
  /** Whether the drawer stands on no conversation. */
  blank: boolean;
  userId: string | undefined;
  startNewConversation: () => void;
  openConversation: (conversationId: string) => void;
  /** Hand the caret to the user. */
  askCaret: () => void;
}

export interface ChatPageOpeningState {
  /** The opening as it is being answered right now, or null. */
  answering: AnsweringOpening | null;
  /**
   * Every time the drawer was sent to the page's opening question. The composer
   * rattles once per ask, so somebody who clicked a button on the page sees
   * where the answer to it goes.
   */
  attention: number;
  /** Take the user to this page's opening question. */
  goToOpening: () => void;
  /** A message went through: if it answered the opening, it has been taken up. */
  sent: () => void;
}

/**
 * The page's opening question or summary in the drawer: where it is answered,
 * and when the drawer steps aside for it. Only the two openings that create a
 * conversation; a nudge is a card with a button, and answering it is not a
 * thing anybody can do.
 */
export function useChatPageOpening({
  opening,
  blank,
  userId,
  startNewConversation,
  openConversation,
  askCaret,
}: ChatPageOpeningOptions): ChatPageOpeningState {
  const queryClient = useQueryClient();
  const conversational = opening !== null && opening.kind !== 'nudge' ? opening : null;

  /*
   * The openings that have been taken up here, by their key. From that moment
   * there is a conversation about the meeting, and the page goes on offering
   * the same opening only because what would stop it — a preparation written,
   * a verslag that stops existing — has nothing to do with answering. So this
   * is what says it has been put, and stops it being put a second time.
   */
  const [openingAnswered, setOpeningAnswered] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [attention, setAttention] = useState(0);

  /*
   * Only while the drawer is blank: answering creates a conversation, and from
   * that moment what was drawn is in it as a real message. So this both draws
   * the bubble and decides where the next message goes — one condition,
   * because they are one thing.
   *
   * And only while the page names no conversation for it: the server saying
   * where the answering is already happening is what keeps a reload, or a
   * second visit, from starting a second conversation about one meeting.
   */
  const answering =
    conversational !== null &&
    conversational.conversationId === null &&
    blank &&
    !openingAnswered.has(conversational.key) &&
    userId !== undefined
      ? { ...conversational, userId }
      : null;

  /*
   * With a conversation named, the opening has been taken up already, so that
   * is the place: it is opened the way any conversation in the history is. With
   * none, the place for it is a drawer with nothing in it — created by nobody
   * until something is said.
   */
  const standOnOpening = useCallback(
    (conversation: string | null) => {
      if (conversation === null) {
        startNewConversation();
        return;
      }

      openConversation(conversation);
    },
    [openConversation, startNewConversation],
  );

  const openingKey = conversational?.key ?? null;
  const openingConversationId = conversational?.conversationId ?? null;

  useStandAsideOnce({
    openingKey,
    openingConversationId,
    openingAnswered,
    standOnOpening,
  });

  /*
   * The place first, then the caret, then the flash that says both have moved.
   * What is written next belongs to the conversation about this page, not to
   * the one the drawer had stepped away to.
   */
  const goToOpening = useCallback(() => {
    standOnOpening(openingConversationId);
    askCaret();
    setAttention((shown) => shown + 1);
  }, [standOnOpening, openingConversationId, askCaret]);

  useAnswerPageAsks(goToOpening);

  /*
   * The meeting has its conversation now. The page beside the drawer is told
   * so: what it knows about the opening decides whether it keeps asking after
   * the verslag being written — and a page still holding the unanswered
   * question would never start.
   */
  const sent = () => {
    if (answering) {
      setOpeningAnswered((current) => new Set(current).add(answering.key));
      void queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
    }
  };

  return { answering, attention, goToOpening, sent };
}

/**
 * A page with an opening gets the drawer it needs for it, even where a
 * conversation from this morning is still running — it is about the page the
 * user just opened, and answering it in yesterday's sitting would be answering
 * it in the wrong place.
 *
 * The same stepping aside as "Nieuwe chat": nothing is created and nothing is
 * closed, so the sitting that was running is one click away in the history.
 * Once per page, so closing it is closing it — and never for one already
 * answered here.
 */
function useStandAsideOnce({
  openingKey,
  openingConversationId,
  openingAnswered,
  standOnOpening,
}: {
  openingKey: string | null;
  openingConversationId: string | null;
  openingAnswered: ReadonlySet<string>;
  standOnOpening: (conversation: string | null) => void;
}): void {
  /*
   * Which page's opening has already been put in front of the user. Cleared as
   * soon as no page is offering one, so coming back to the page shows it again.
   */
  const openerShownFor = useRef<string | null>(null);

  useEffect(() => {
    if (openingKey === null) {
      openerShownFor.current = null;
      return;
    }

    if (openingKey === openerShownFor.current || openingAnswered.has(openingKey)) {
      return;
    }

    openerShownFor.current = openingKey;
    standOnOpening(openingConversationId);
  }, [openingKey, openingConversationId, openingAnswered, standOnOpening]);
}

/**
 * The opening asked for from the page beside the drawer, where the button that
 * asks has nothing else to show for itself: on a desktop the drawer is already
 * standing open.
 *
 * Counted against what the drawer has answered rather than run on every
 * change, or the conversation arriving from the server would flash the panel
 * at somebody who asked for nothing.
 */
function useAnswerPageAsks(goToOpening: () => void): void {
  const openingAsked = useChatOpeningAsked();
  const answeredAsk = useRef(openingAsked);

  useEffect(() => {
    if (openingAsked === answeredAsk.current) return;
    answeredAsk.current = openingAsked;
    goToOpening();
  }, [openingAsked, goToOpening]);
}
